<template>
  <div :class="[getItemTableContainerClass]">
    <ItemTable
      ref="itemTable"
      :columns="columns"
      details
      :handleBulkDelete="handleBulkDelete"
      :handleDetailsButtonClick="handleRowClick"
      :handleRowClick="handleRowClick"
      :handleSettingsButtonClick="handleSettingsButtonClick"
      :hasSelectColumn="true"
      :isMainActionDisabled="isMainActionDisabled"
      :itemType="itemType"
      :keepFresh="false"
      :mainActionMenuItems="mainActionMenuItems"
      mainActionMenuLabel="Select Resource for Policy"
      :preventSearchReset="true"
      :route="route"
      :selectionItemText="policySelectionText"
      :showActionBar="true"
      :showBound="false"
      :showBulkDelete="true"
      :showMainAction="true"
      :showSearchForm="$hasFeatureFlag($featureFlags.authNPolicyFilters)"
      :showSettingsButton="true"
      :useDropDownFilterComponent="
        $hasFeatureFlag($featureFlags.authNPolicyFilters) ? useDropDownFilterComponent : null"
      :viewport="viewport"
    />
  </div>
</template>

<script>
import { capitalize } from 'lodash';
import {
  eventLocations,
  eventProperties,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters } from 'vuex';

import * as Constants from 'client/util/Constants';
import { notify } from 'client/util';
import Util from 'client/util/Util';

import ConditionalPolicyStatus from 'client/Security/ConditionalPolicyStatus.vue';
import ConfirmationModal from 'components/Modal/ModalComponents/ConfirmationModal.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';

import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';

import iconGroups from 'public/img/icons/nav/icon-nav-groups.svg?inline';
import iconLdap from 'public/img/icons/nav/icon-nav-ldap.svg?inline';
import iconSso from 'public/img/icons/nav/icon-nav-sso.svg?inline';

const { error, success } = Constants.default;
const {
  BodyComponentConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModuleMap,
} = Constants;
const { columnTypes } = ItemTableConsts;
const { authNPolicies } = ItemTypeConsts;

const authNPoliciesCollection = StoreModuleMap[authNPolicies];
const authNPoliciesRoute = ItemTypeRoutes[authNPolicies];

const {
  description,
  iconName,
  location,
  section,
  text,
} = eventProperties;
const { conditionalPoliciesPage } = eventLocations;

export default {
  name: 'AuthNPoliciesItemTable',

  components: {
    ItemTable,
  },

  mixins: [
    ItemTableComputedMixin,
    ItemTablePropsMixin,
  ],

  props: {
    isMainActionDisabled: Boolean,
  },

  data() {
    return {
      policySelectionText: {
        label: 'selected',
        plural: 'policies',
        singular: 'policy',
      },
      itemType: ItemTypeConsts.authNPolicies,
      panelType: ItemTableConsts.panelType.mainPanel,
      route: authNPoliciesRoute,
      columns: [
        {
          bodyComponent: ConditionalPolicyStatus,
          bodyComponentProps: {
            type: BodyComponentConsts.statusIconTypes.system,
          },
          classNames: {
            body: [columnTypes.statusColumn],
            heading: [columnTypes.statusColumn],
          },
          headingText: 'Status',
          sortBy: 'disabled',
        },
        {
          bodyComponent: 'ItemName',
          bodyComponentProps: {
            titleField: 'name',
          },
          classNames: {
            body: [this.$style.nameColumn],
          },
          headingText: 'Policy Name',
          sortBy: 'name',
        },
        {
          dataField: 'typeLabel',
          headingText: 'Policy Type',
          sortBy: 'type',
        },
        {
          dataField: 'actionLabel',
          headingText: 'Action',
          sortBy: 'action',
        },
      ],
    };
  },

  computed: {
    ...mapGetters(authNPoliciesCollection, {
      selectedItems: 'getSelectedItems',
    }),

    mainActionMenuItems() {
      const defaultMenuItems = [
        {
          clickHandler: () => this.routeToNewWithType('userportal'),
          component: iconGroups,
          text: 'User Portal',
        },
        {
          clickHandler: () => this.routeToNewWithType('application'),
          component: iconSso,
          text: 'SSO Applications',
        },
        {
          clickHandler: () => this.routeToNewWithType('ldap'),
          component: iconLdap,
          text: 'JumpCloud LDAP',
        },
      ];

      return defaultMenuItems;
    },
  },

  ModalEventBus,

  notify,

  methods: {
    ...mapActions(authNPoliciesCollection, [
      'deleteItems',
    ]),

    deleteConfirmModal(modalConfig = {}) {
      return this.$options.ModalEventBus.open(modalConfig);
    },

    handleBulkDelete() {
      if (this.selectedItems.length < 1) return undefined;

      const {
        $options, deleteConfirmModal, deleteItems, selectedItems,
      } = this;
      const noun = selectedItems.length > 1 ? 'policies' : 'policy';
      const verb = selectedItems.length > 1 ? 'have' : 'has';

      EventHelper.triggerButtonClicked({
        [description]: 'Delete button clicked',
        [location]: conditionalPoliciesPage,
        [text]: 'delete',
        [section]: 'policies Item Table',
      });

      return deleteConfirmModal({
        component: ConfirmationModal,
        componentProps: {
          mdTemplate: `**Are you sure** you would like to delete the selected ${noun}? Deletion will permanently remove the ${noun} and another policy or default policy will apply.`,
          continueText: 'confirm',
          handleContinue: () => deleteItems(selectedItems)
            .then((res) => {
              EventHelper.triggerButtonClicked({
                [description]: 'Confirm deletion of Conditional Policy',
                [location]: `${conditionalPoliciesPage} Delete Policy Dialog Box`,
                [text]: 'confirm',
                [section]: 'bottom',
              });
              if (res.failedTasks && res.failedTasks.length > 0) {
                return $options.notify(error, `There was an error trying to delete the Conditional Access ${capitalize(noun)}. Try again.`);
              }

              return $options.notify(success, `The Conditional Access ${capitalize(noun)} ${verb} been successfully deleted.`);
            })
            .finally(() => $options.ModalEventBus.close()),
          headerText: `Delete ${capitalize(noun)}`,
          mainActionAttention: true,
          handleCancel: () => {
            EventHelper.triggerLinkClicked({
              [description]: `Closes ${conditionalPoliciesPage} Delete Policy Dialog Box`,
              [location]: `${conditionalPoliciesPage} Delete Policy Dialog Box`,
              [text]: 'cancel',
              [section]: 'bottom',
            });
            $options.ModalEventBus.close();
          },
          handleCancelX: () => {
            EventHelper.triggerIconClicked({
              [description]: `Closes ${conditionalPoliciesPage} Delete Policy Dialog Box`,
              [iconName]: 'close',
              [location]: `${conditionalPoliciesPage} Delete Policy Dialog Box`,
              [text]: 'X',
              [section]: 'upper right hand corner',
            });
            $options.ModalEventBus.close();
          },
        },
      });
    },

    handleRowClick(itemId) {
      Util.redirect(`${authNPoliciesRoute}/${itemId}`);
    },

    handleSettingsButtonClick() {
      Util.redirect(
        ItemTypeRoutes[ItemTypeConsts.authNPoliciesSettings],
      );
    },

    routeToNewWithType(type) {
      Util.redirect(`${authNPoliciesRoute}/new?type=${type}`);
    },
  },
};
</script>

<style module>
@import './SecuritySection.css';

.nameColumn {
  max-width: 350px;
}
</style>
